import { useCallback, useEffect, useRef, useState } from 'react'
import { anchorKey, PREVIEW_PARAM } from '../data/designVariants'
import { useDesignVariants } from '../contexts/DesignVariantContext'
import type { DesignId, DesignVariantConfig, SurfaceKey } from '../types/experience'

/**
 * The design variant resolver — the ONE place a presented design is decided.
 *
 * Homepage and Housing do not own timers. They ask this hook which design to
 * present for their surface and render it. What the hook is responsible for:
 *
 *   • resolving the administrator's published configuration into a design;
 *   • owning exactly one clock per surface, cleared on unmount;
 *   • offsetting each visitor's cycle from its own anchor, so the whole
 *     platform does not turn over at the same instant;
 *   • deferring a handover while someone is mid-interaction;
 *   • never presenting a withdrawn design, and never presenting nothing.
 *
 * It changes which presentation renders. It does not navigate, change the
 * URL, reload the browser or touch product state.
 */

export type DesignPreview = 'auto' | DesignId

const OTHER: Record<DesignId, DesignId> = {
  DESIGN_1: 'DESIGN_2',
  DESIGN_2: 'DESIGN_1',
}

function enabled(config: DesignVariantConfig, design: DesignId): boolean {
  return design === 'DESIGN_1' ? config.design1Enabled : config.design2Enabled
}

/** Never returns a withdrawn design, and never returns nothing. */
function usable(config: DesignVariantConfig, wanted: DesignId): DesignId {
  if (enabled(config, wanted)) return wanted
  const alternative = OTHER[wanted]
  if (enabled(config, alternative)) return alternative
  return config.fallbackDesign
}

/**
 * Each visitor gets their own anchor, so two people who arrived ten minutes
 * apart hand over ten minutes apart. Tabs in one browser share it, because
 * tabs disagreeing has no explanation a visitor would accept.
 */
function readAnchor(surface: SurfaceKey): number {
  const now = Date.now()
  if (typeof window === 'undefined') return now
  const key = anchorKey(surface)
  try {
    const raw = window.localStorage.getItem(key)
    const stored = raw ? Number(raw) : NaN
    if (Number.isFinite(stored) && stored <= now) return stored
    window.localStorage.setItem(key, String(now))
    return now
  } catch {
    return now
  }
}

function readPreview(): DesignPreview | null {
  if (typeof window === 'undefined') return null
  const value = new URLSearchParams(window.location.search).get(PREVIEW_PARAM)
  if (!value) return null
  const normalised = value.trim().toLowerCase()
  if (normalised === 'rotation' || normalised === 'auto') return 'auto'
  if (normalised === '1' || normalised === 'a' || normalised === 'design-1') return 'DESIGN_1'
  if (normalised === '2' || normalised === 'b' || normalised === 'design-2') return 'DESIGN_2'
  return null
}

/**
 * True while someone is in the middle of something a handover would destroy:
 * typing, a menu open, a dialog open, or a surface asking to be left alone.
 */
function interactionInProgress(): boolean {
  if (typeof document === 'undefined') return false
  if (document.visibilityState === 'hidden') return true

  const active = document.activeElement as HTMLElement | null
  if (active) {
    const tag = active.tagName
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
    if (active.isContentEditable) return true
    if (active.getAttribute('aria-expanded') === 'true') return true
  }

  if (document.querySelector('[role="dialog"], [aria-modal="true"], dialog[open]')) return true
  if (document.querySelector('[data-design-hold="true"]')) return true
  return false
}

/** The design a configuration says should be showing at a given moment. */
export function resolveDesign(
  config: DesignVariantConfig,
  anchor: number,
  now: number,
): { design: DesignId; nextChangeAt: number | null } {
  if (config.mode === 'DESIGN_1' || config.mode === 'DESIGN_2') {
    return { design: usable(config, config.mode), nextChangeAt: null }
  }

  if (config.rotationPaused) {
    return { design: usable(config, config.pausedOn ?? config.rotationStartDesign), nextChangeAt: null }
  }

  const interval = config.rotationIntervalMs
  const elapsed = Math.max(0, now - anchor)
  const steps = Math.floor(elapsed / interval)
  const design = steps % 2 === 0 ? config.rotationStartDesign : OTHER[config.rotationStartDesign]

  // A withdrawn design does not get a turn: the surface simply stays on the
  // one that is available, rather than flickering into nothing.
  if (!enabled(config, design)) {
    return { design: usable(config, design), nextChangeAt: null }
  }

  return { design, nextChangeAt: anchor + (steps + 1) * interval }
}

interface DesignVariantState {
  design: DesignId
  /** The design whose assets are worth warming while this one is showing. */
  upcoming: DesignId
  config: DesignVariantConfig
  ready: boolean
  preview: DesignPreview
  setPreview: (preview: DesignPreview) => void
  nextChangeAt: number | null
}

export function useDesignVariant(surface: SurfaceKey): DesignVariantState {
  const { configFor, ready } = useDesignVariants()
  const config = configFor(surface)

  const anchor = useRef<number>(readAnchor(surface))
  const [preview, setPreview] = useState<DesignPreview>(() => readPreview() ?? 'auto')

  const target = useCallback(
    () =>
      preview !== 'auto'
        ? { design: preview, nextChangeAt: null }
        : resolveDesign(config, anchor.current, Date.now()),
    [config, preview],
  )

  const [presented, setPresented] = useState<DesignId>(() => target().design)
  const presentedRef = useRef(presented)
  presentedRef.current = presented

  useEffect(() => {
    // A pinned design and a paused rotation have no clock at all, rather than
    // a clock whose result is thrown away.
    const wanted = target()
    if (wanted.design !== presentedRef.current && preview !== 'auto') {
      setPresented(wanted.design)
      return undefined
    }
    if (wanted.nextChangeAt === null) {
      if (wanted.design !== presentedRef.current) setPresented(wanted.design)
      return undefined
    }

    // Configuration arriving, or an administrator publishing, applies at once
    // rather than waiting out a tick.
    if (wanted.design !== presentedRef.current && !interactionInProgress()) {
      setPresented(wanted.design)
    }

    const tick = window.setInterval(() => {
      const next = target()
      if (next.design === presentedRef.current) return
      if (interactionInProgress()) return
      setPresented(next.design)
    }, 1000)

    return () => window.clearInterval(tick)
  }, [target, preview])

  return {
    design: presented,
    upcoming: OTHER[presented],
    config,
    ready,
    preview,
    setPreview,
    nextChangeAt: target().nextChangeAt,
  }
}
